import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TablePagination, 
  Chip, 
  TextField, 
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Autocomplete,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import TableChartIcon from '@mui/icons-material/TableChart';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB } from 'date-fns/locale';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { AppointmentStatus, Appointment as AppointmentType, GetAppointmentsQueryParams } from '../../features/appointments/types/appointment.types';
import { appointmentApi } from '../../features/appointments/api/appointmentApi';
import { hospitalApi } from '../../api/hospitalApi';
import { exportAppointmentDataToCSV } from '../../utils/csvExport';

// Using the AppointmentType from the API
type Appointment = AppointmentType & {
  serviceName?: string;
  duration?: number;
};

const AppointmentReport: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today in local timezone
    return today;
  });
  const [endDate, setEndDate] = useState<Date | null>(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today in local timezone
    return today;
  });
  const [statusFilter, setStatusFilter] = useState<AppointmentStatus | 'all'>('all');
  const [doctorFilter, setDoctorFilter] = useState<string>('all');
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; name: string } | null>(null);
  
  // Appointment data state
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  
  // Ref for PDF generation
  const reportRef = React.useRef<HTMLDivElement>(null);
  
  // Hospital info state
  const [hospitalInfo, setHospitalInfo] = useState<any>(null);
  
  // PDF menu state
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Doctors list
  const [doctors, setDoctors] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    import('../../features/employees/api/employeeApi').then(({ employeeApi }) => {
      employeeApi.getDoctors().then((data) => {
        setDoctors(data.map(doc => ({ id: doc.id, name: `${doc.firstName} ${doc.lastName}`.trim() }))); 
      });
    });
  }, []);

  // Fetch hospital info on component mount
  useEffect(() => {
    const fetchHospitalInfo = async () => {
      try {
        const hospital = await hospitalApi.getCompanyProfile();
        setHospitalInfo(hospital);
      } catch (error) {
        console.error('Error fetching hospital info:', error);
      }
    };
    fetchHospitalInfo();
  }, []);

  // Patients list for patient filter
  const [patients, setPatients] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => {
    import('../../features/patients/api/patientApi').then(({ patientApi }) => {
      patientApi.getPatients({ page: 1, limit: 100 }).then((resp) => {
        setPatients(resp.data.map((p: any) => ({ id: p.id, name: p.fullName }))); 
      });
    });
  }, []);

  // Fetch appointments from API
  const fetchAppointments = async () => {
    setLoading(true);
    try {
      const params: GetAppointmentsQueryParams = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
      };
      
      // Add date filters using local timezone
      if (startDate) {
        const localStart = new Date(startDate);
        localStart.setHours(0, 0, 0, 0);
        params.startDate = localStart.toISOString();
      }
      if (endDate) {
        const localEnd = new Date(endDate);
        localEnd.setHours(23, 59, 59, 999);
        params.endDate = localEnd.toISOString();
      }
      
      // Add status filter
      if (statusFilter !== 'all') {
        params.status = [statusFilter];
      }
      
      // Add doctor filter
      if (doctorFilter !== 'all') {
        params.doctorId = doctorFilter;
      }
      
      // Add patient filter
      if (selectedPatient) {
        params.patientId = selectedPatient.id;
      }
      
      console.log('Fetching appointments with params:', params);
      
      const response = await appointmentApi.getAll(params);
      
      console.log('Appointments fetched:', response);
      
      setAppointments(response.data);
      setTotalCount(response.meta.total);
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch appointments on component mount and when filters change
  useEffect(() => {
    fetchAppointments();
  }, [page, rowsPerPage, startDate, endDate, statusFilter, doctorFilter, selectedPatient]);

  // Since filtering is now done on the server side, we use appointments directly
  const filteredAppointments = appointments;

  // Calculate summary statistics using totalCount from API for accurate pagination
  const totalAppointments = totalCount;
  const statusCounts = appointments.reduce((counts: { [key: string]: number }, appointment) => {
    const status = appointment.status;
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});

  // Status labels for display
  const statusLabels: { [key: string]: string } = {
    'SCHEDULED': 'Scheduled',
    'CONFIRMED': 'Confirmed',
    'ARRIVED': 'Arrived',
    'IN_PROGRESS': 'In Progress',
    'COMPLETED': 'Completed',
    'CANCELLED': 'Cancelled',
    'NO_SHOW': 'No Show',
    'RESCHEDULED': 'Rescheduled'
  };

  const generateExcelReport = () => {
    // Create Excel data structure (CSV format for consistency)
    const reportData = [];
    
    // Header information
    reportData.push(['Appointment Report']);
    reportData.push(['Generated on:', new Date().toLocaleDateString('en-IN')]);
    reportData.push(['Date Range:', `${startDate?.toLocaleDateString('en-IN')} to ${endDate?.toLocaleDateString('en-IN')}`]);
    reportData.push(['Total Appointments:', filteredAppointments.length]);
    reportData.push([]);
    
    // Table headers
    reportData.push(['APPOINTMENT DETAILS']);
    reportData.push([
      'Appointment Number',
      'Patient Name',
      'Doctor Name',
      'Appointment Type',
      'Appointment Date',
      'Start Time',
      'End Time',
      'Duration (min)',
      'Status',
      'Priority',
      'Notes'
    ]);
    
    // Table data
    filteredAppointments.forEach(apt => {
      reportData.push([
        apt.appointmentNumber,
        apt.patientName,
        apt.doctorName,
        apt.type?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'N/A',
        apt.date || formatDateForDisplay(apt.appointmentDate),
        apt.time || (apt.startTime ? new Date(apt.startTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'),
        apt.endTime ? new Date(apt.endTime).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A',
        apt.startTime && apt.endTime 
          ? Math.round((new Date(apt.endTime).getTime() - new Date(apt.startTime).getTime()) / 60000)
          : 'N/A',
        apt.status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()),
        apt.priority?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'Medium',
        apt.notes || apt.reason || 'N/A'
      ]);
    });
    
    // Convert to CSV format
    const csvContent = reportData.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\r\n');
    
    // Download CSV file with Excel naming
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `AppointmentReport_${new Date().toISOString().slice(0,10)}.xlsx`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToCSV = async () => {
    try {
      // Show loading state
      setLoading(true);
      
      // Fetch ALL appointments with current filters (not just current page)
      const allFilteredAppointments = await appointmentApi.getAll({
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
        status: statusFilter && statusFilter !== 'all' ? [statusFilter as AppointmentStatus] : undefined,
        doctorId: doctorFilter && doctorFilter !== 'all' ? doctorFilter : undefined,
        patientId: selectedPatient?.id || undefined,
        page: 1,
        limit: 10000 // Get all appointments, not just current page
      });
      
      console.log(`Exporting ${allFilteredAppointments.data.length} appointments (Total: ${allFilteredAppointments.meta.total})`);
      
      // Prepare data for comprehensive export
      const allOrders: any[] = [];
      const allQuestions: any[] = [];
      const allConsultations: any[] = [];
      
      // Fetch additional data for each appointment
      for (const appointment of allFilteredAppointments.data) {
        try {
          // Fetch orders for this appointment
          const { appointmentOrdersApi } = await import('../../features/appointments/api/appointmentOrdersApi');
          const orders = await appointmentOrdersApi.getOrdersByAppointment(appointment.id);
          
          // Add appointment reference to orders
          const ordersWithRef = orders.map((order: any) => ({
            ...order,
            appointment: {
              appointmentNumber: appointment.appointmentNumber || appointment.id,
              patient: appointment.patient,
              patientName: appointment.patientName || appointment.patient?.fullName
            }
          }));
          allOrders.push(...ordersWithRef);
          
          // Fetch consultations for this appointment
          try {
            const { consultationApi } = await import('../../features/appointments/api/consultationApi');
            const consultation = await consultationApi.getByAppointmentId(appointment.id);
            
            if (consultation) {
              // Format consultation data
              const consultationWithRef = {
                ...consultation,
                appointmentNumber: appointment.appointmentNumber || appointment.id,
                patientName: appointment.patientName || appointment.patient?.fullName,
                doctorName: `Dr. ${appointment.doctor?.firstName || ''} ${appointment.doctor?.lastName || ''}`.trim()
              };
              allConsultations.push(consultationWithRef);
              
              // Add questions from consultation
              if (consultation.questions && consultation.questions.length > 0) {
                const questionsWithRef = consultation.questions.map(q => ({
                  ...q,
                  appointmentNumber: appointment.appointmentNumber || appointment.id,
                  patientName: appointment.patientName || appointment.patient?.fullName
                }));
                allQuestions.push(...questionsWithRef);
              }
            }
          } catch (consultationError) {
            console.log(`No consultation data for appointment ${appointment.id}`);
          }
          
        } catch (error) {
          console.error(`Error fetching data for appointment ${appointment.id}:`, error);
        }
      }
      
      // Use comprehensive CSV export
      const success = exportAppointmentDataToCSV({
        appointments: allFilteredAppointments.data,
        orders: allOrders,
        questions: allQuestions,
        consultations: allConsultations
      }, 'AppointmentReport');
      
      if (success) {
        console.log('CSV export completed successfully');
      } else {
        alert('Error exporting CSV. Please try again.');
      }
      
    } catch (error) {
      console.error('Error during CSV export:', error);
      alert('Error exporting CSV. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    const today = new Date();
    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);
    const endOfToday = new Date(today);
    endOfToday.setHours(23, 59, 59, 999);
    
    setStartDate(startOfToday);
    setEndDate(endOfToday);
    setStatusFilter('all');
    setDoctorFilter('all');
    setSelectedPatient(null);
    setPage(0); // Reset pagination
  };
  
  const refreshData = () => {
    setPage(0); // Reset to first page when refreshing
    fetchAppointments();
  };

  const handleDownloadPDF = async (includeAllData = false) => {
    if (!reportRef.current) return;
    
    try {
      let originalRowsPerPage = rowsPerPage;
      let originalPage = page;
      
      if (includeAllData) {
        // Temporarily show all data for PDF generation
        setRowsPerPage(totalCount);
        setPage(0);
        
        // Wait for re-render
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Create PDF with header
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      // Use hospital info from API or fallback to default
      const pdfHospitalInfo = hospitalInfo || {
        name: "Airyaman Healthcare",
        address: "123 Healthcare Street, Medical District, City - 123456",
        phoneNumber: "+91 12345 67890",
        email: "info@airyaman.com"
      };
      
      // Header section with logo
      try {
        // Load and add logo
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        
        await new Promise((resolve, reject) => {
          logoImg.onload = () => {
            // Add logo (16x16mm at 20,10 position)
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = logoImg.width;
            canvas.height = logoImg.height;
            ctx?.drawImage(logoImg, 0, 0);
            const logoDataUrl = canvas.toDataURL('image/jpeg', 0.8);
            
            pdf.addImage(logoDataUrl, 'JPEG', 20, 10, 16, 16);
            resolve(true);
          };
          logoImg.onerror = () => {
            console.warn('Logo not found, continuing without logo');
            resolve(false);
          };
          logoImg.src = '/logo.jpeg';
        });
      } catch (error) {
        console.warn('Error loading logo:', error);
      }
      
      // Hospital name and info (positioned next to logo)
      pdf.setTextColor(0, 0, 0); // Set text color to black
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.text(pdfHospitalInfo.name, 40, 20);
      
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const address = pdfHospitalInfo.address || 'Address not available';
      const phone = pdfHospitalInfo.phoneNumber || 'Phone not available';
      const email = pdfHospitalInfo.email || 'Email not available';
      
      pdf.text(address, 40, 28);
      pdf.text(`Phone: ${phone}`, 40, 35);
      pdf.text(`Email: ${email}`, 40, 42);
      
      // Title
      pdf.setFontSize(16);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Appointment Report', 20, 65);
      
      // Date range
      const fromDateStr = startDate ? startDate.toLocaleDateString() : 'N/A';
      const toDateStr = endDate ? endDate.toLocaleDateString() : 'N/A';
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`Period: ${fromDateStr} to ${toDateStr}`, 20, 75);
      
      // Add line separator
      pdf.line(20, 80, 190, 80);
      
      // Create canvas from the report content (excluding filters)
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        onclone: (clonedDoc) => {
          // Force all text to be black for PDF visibility
          const allElements = clonedDoc.querySelectorAll('*');
          allElements.forEach((element: any) => {
            if (element.style) {
              element.style.color = 'black';
              element.style.setProperty('color', 'black', 'important');
            }
          });
        }
      });
      
      const imgData = canvas.toDataURL('image/png');
      const imgWidth = 170; // Reduced width to fit with margins
      const pageHeight = 200; // Available height after header
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      
      let position = 85; // Start after header
      
      // Add first page content
      pdf.addImage(imgData, 'PNG', 20, position, imgWidth, Math.min(imgHeight, pageHeight));
      heightLeft -= pageHeight;
      
      // Add additional pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        
        // Add header to subsequent pages
        pdf.setTextColor(0, 0, 0); // Ensure black text on continuation pages
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Appointment Report (Continued)', 20, 20);
        pdf.line(20, 25, 190, 25);
        
        position = heightLeft - imgHeight + 30; // Adjust for new page header
        pdf.addImage(imgData, 'PNG', 20, position, imgWidth, Math.min(heightLeft, 240));
        heightLeft -= 240;
      }
      
      const filename = `AppointmentReport_${fromDateStr.replace(/\//g, '-')}_to_${toDateStr.replace(/\//g, '-')}.pdf`;
      pdf.save(filename);
      
      // Restore original pagination if we changed it
      if (includeAllData) {
        setRowsPerPage(originalRowsPerPage);
        setPage(originalPage);
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  const handleDoctorFilterChange = (event: SelectChangeEvent) => {
    setDoctorFilter(event.target.value);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: AppointmentStatus) => {
    switch (status) {
      case AppointmentStatus.COMPLETED: return 'success';
      case AppointmentStatus.SCHEDULED:
      case AppointmentStatus.CONFIRMED:
      case AppointmentStatus.ARRIVED: return 'info';
      case AppointmentStatus.IN_PROGRESS: return 'primary';
      case AppointmentStatus.CANCELLED: return 'error';
      case AppointmentStatus.NO_SHOW: return 'warning';
      case AppointmentStatus.RESCHEDULED: return 'secondary';
      default: return 'default';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
      <Box sx={{ p: 3 }} ref={reportRef}>
        <Typography variant="h4" gutterBottom>
          Appointment Report
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          View all appointments with their current status
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              {/* First Row: Dates, Status, Doctor */}
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="From Date"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DatePicker
                  label="To Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="dd/MM/yyyy"
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value as AppointmentStatus | 'all')}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    <MenuItem value={AppointmentStatus.SCHEDULED}>Scheduled</MenuItem>
                    <MenuItem value={AppointmentStatus.CONFIRMED}>Confirmed</MenuItem>
                    <MenuItem value={AppointmentStatus.ARRIVED}>Arrived</MenuItem>
                    <MenuItem value={AppointmentStatus.IN_PROGRESS}>In Progress</MenuItem>
                    <MenuItem value={AppointmentStatus.COMPLETED}>Completed</MenuItem>
                    <MenuItem value={AppointmentStatus.CANCELLED}>Cancelled</MenuItem>
                    <MenuItem value={AppointmentStatus.NO_SHOW}>No Show</MenuItem>
                    <MenuItem value={AppointmentStatus.RESCHEDULED}>Rescheduled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Doctor</InputLabel>
                  <Select
                    value={doctorFilter}
                    label="Doctor"
                    onChange={handleDoctorFilterChange}
                  >
                    <MenuItem value="all">All Doctors</MenuItem>
                    {doctors.map((doctor) => (
                      <MenuItem key={doctor.id} value={doctor.id}>
                        {doctor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Second Row: Patient, Buttons */}
              <Grid item xs={12} md={3}>
                <Autocomplete
                  options={patients}
                  value={selectedPatient}
                  onChange={(_, value) => setSelectedPatient(value)}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Patient" size="small" fullWidth />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={9} display="flex" justifyContent="flex-end" alignItems="center">
                <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<TableChartIcon />}
                    onClick={exportToCSV}
                    disabled={loading}
                    sx={{ height: '40px' }}
                  >
                    {loading ? 'Exporting...' : 'Export Excel'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PictureAsPdfIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
                    sx={{ height: '40px' }}
                  >
                    Download PDF
                  </Button>
                  <Menu
                    anchorEl={pdfMenuAnchor}
                    open={Boolean(pdfMenuAnchor)}
                    onClose={() => setPdfMenuAnchor(null)}
                  >
                    <MenuItem onClick={() => { handleDownloadPDF(false); setPdfMenuAnchor(null); }}>
                      <ListItemIcon>
                        <PictureAsPdfIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Current Page Only" />
                    </MenuItem>
                    <MenuItem onClick={() => { handleDownloadPDF(true); setPdfMenuAnchor(null); }}>
                      <ListItemIcon>
                        <PictureAsPdfIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary="Complete Report" />
                    </MenuItem>
                  </Menu>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    sx={{ height: '40px' }}
                    onClick={refreshData}
                    disabled={loading}
                    startIcon={<RefreshIcon />}
                  >
                    {loading ? 'Loading...' : 'Refresh'}
                  </Button>
                  <Button
                    variant="outlined"
                    color="secondary"
                    sx={{ height: '40px' }}
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Summary Section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} md={2}>
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" color="primary.main">
                    {totalAppointments}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Appointments
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Status-wise breakdown */}
            {Object.entries(statusCounts).map(([status, count]) => {
              const getStatusColor = (status: string) => {
                switch (status) {
                  case 'COMPLETED': return 'success.main';
                  case 'SCHEDULED':
                  case 'CONFIRMED':
                  case 'ARRIVED': return 'info.main';
                  case 'IN_PROGRESS': return 'primary.main';
                  case 'CANCELLED': return 'error.main';
                  case 'NO_SHOW': return 'warning.main';
                  case 'RESCHEDULED': return 'secondary.main';
                  default: return 'text.primary';
                }
              };

              return (
                <Grid item xs={6} md={2} key={status}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" color={getStatusColor(status)}>
                        {count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {statusLabels[status] || status}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Appointment ID</TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Doctor</TableCell>
                <TableCell>Service</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAppointments.length > 0 ? (
                filteredAppointments
                  .map((appointment) => (
                    <TableRow key={appointment.id}>
                      <TableCell>{appointment.appointmentNumber}</TableCell>
                      <TableCell>{appointment.patientName}</TableCell>
                      <TableCell>{appointment.doctorName}</TableCell>
                      <TableCell>{appointment.type?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase()) || 'N/A'}</TableCell>
                      <TableCell>
                        {appointment.date} {appointment.time}
                      </TableCell>
                      <TableCell>
                        {appointment.startTime && appointment.endTime 
                          ? Math.round((new Date(appointment.endTime).getTime() - new Date(appointment.startTime).getTime()) / 60000) + ' min'
                          : 'N/A'
                        }
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={appointment.status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())} 
                          size="small"
                          sx={{ color: 'black !important' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    {loading ? (
                      <Typography variant="body2" color="text.secondary">
                        Loading appointments...
                      </Typography>
                    ) : (
                      <Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          No appointments found for the selected criteria
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Try adjusting your date range or filters
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Doctor Wise Summary */}
        {appointments.length > 0 && (
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Doctor Wise Summary
            </Typography>
            <Grid container spacing={2}>
              {(() => {
                // Calculate doctor wise totals
                const doctorTotals = appointments.reduce((acc, appointment) => {
                  const doctorName = appointment.doctorName || 'Unknown Doctor';
                  if (!acc[doctorName]) {
                    acc[doctorName] = {
                      totalAppointments: 0,
                      scheduledCount: 0,
                      arrivedCount: 0,
                      inProgressCount: 0,
                      completedCount: 0,
                      cancelledCount: 0,
                      patientCount: new Set(),
                      totalDuration: 0,
                      avgDuration: 0
                    };
                  }
                  acc[doctorName].totalAppointments += 1;
                  acc[doctorName].patientCount.add(appointment.patientName);
                  
                  // Count by status
                  switch (appointment.status) {
                    case 'SCHEDULED':
                      acc[doctorName].scheduledCount += 1;
                      break;
                    case 'ARRIVED':
                      acc[doctorName].arrivedCount += 1;
                      break;
                    case 'IN_PROGRESS':
                      acc[doctorName].inProgressCount += 1;
                      break;
                    case 'COMPLETED':
                      acc[doctorName].completedCount += 1;
                      break;
                    case 'CANCELLED':
                      acc[doctorName].cancelledCount += 1;
                      break;
                  }
                  
                  // Calculate duration if available
                  if (appointment.startTime && appointment.endTime) {
                    const duration = Math.round((new Date(appointment.endTime).getTime() - new Date(appointment.startTime).getTime()) / 60000);
                    acc[doctorName].totalDuration += duration;
                  }
                  
                  return acc;
                }, {} as Record<string, {
                  totalAppointments: number;
                  scheduledCount: number;
                  arrivedCount: number;
                  inProgressCount: number;
                  completedCount: number;
                  cancelledCount: number;
                  patientCount: Set<string>;
                  totalDuration: number;
                  avgDuration: number;
                }>);

                // Calculate average duration for each doctor
                Object.keys(doctorTotals).forEach(doctorName => {
                  const completedAppts = doctorTotals[doctorName].completedCount;
                  if (completedAppts > 0) {
                    doctorTotals[doctorName].avgDuration = Math.round(doctorTotals[doctorName].totalDuration / completedAppts);
                  }
                });

                // Sort doctors by total appointments (descending)
                const sortedDoctors = Object.entries(doctorTotals)
                  .sort(([,a], [,b]) => b.totalAppointments - a.totalAppointments);

                return sortedDoctors.map(([doctorName, totals]) => (
                  <Grid item xs={12} md={6} lg={4} key={doctorName}>
                    <Card>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                          {doctorName}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Total Appointments:
                          </Typography>
                          <Typography variant="body2" color="primary.main" fontWeight="bold">
                            {totals.totalAppointments}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Completed:
                          </Typography>
                          <Typography variant="body2" color="success.main" fontWeight="bold">
                            {totals.completedCount}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Arrived:
                          </Typography>
                          <Typography variant="body2" color="success.light" fontWeight="bold">
                            {totals.arrivedCount}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            In Progress:
                          </Typography>
                          <Typography variant="body2" color="warning.main" fontWeight="bold">
                            {totals.inProgressCount}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Scheduled:
                          </Typography>
                          <Typography variant="body2" color="info.main" fontWeight="bold">
                            {totals.scheduledCount}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Cancelled:
                          </Typography>
                          <Typography variant="body2" color="error.main" fontWeight="bold">
                            {totals.cancelledCount}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Unique Patients:
                          </Typography>
                          <Typography variant="body2">
                            {totals.patientCount.size}
                          </Typography>
                        </Box>
                        {totals.avgDuration > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              Avg Duration:
                            </Typography>
                            <Typography variant="body2">
                              {totals.avgDuration} min
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ));
              })()}
            </Grid>
          </Box>
        )}
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
    </LocalizationProvider>
  );
};

export default AppointmentReport;
